import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Plus, Save, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ModalSuccess } from './ModalSuccess';
import { useTeachers } from '../../hooks/useTeachers';
import { teachersStore } from '../../services/teachersStore';
import { trainingCategories, trainingProducts } from '../../data/trainingProducts';
import { parseAmount } from '../../utils/currency';
import { teacherCategories } from '../../utils/teachers';
import { Teacher, TeacherBillingMethod } from '../../types/teacher';
interface TeacherFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacher?: Teacher | null;
}
type Draft = Omit<Teacher, 'id' | 'hourlyRate'> & {
  id?: string;
  hourlyRate?: number;
};
const emptyDraft = (): Draft => ({
  firstName: '',
  lastName: '',
  fiscalCode: '',
  email: '',
  phone: '',
  zones: '',
  limitedAvailability: '',
  productCodes: [],
  hourlyRate: undefined,
  agreements: '',
  privateEquipment: [],
  expenseReimbursement: '',
  billingMethod: 'Fattura',
  documents: {}
});
const billingMethods: TeacherBillingMethod[] = ['Fattura', "Ritenuta d'acconto"];
export function TeacherFormModal({
  isOpen,
  onClose,
  teacher
}: TeacherFormModalProps) {
  const allTeachers = useTeachers();
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [equipmentInput, setEquipmentInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    setDraft(teacher ? {
      ...teacher
    } : emptyDraft());
    setEquipmentInput('');
    setErrors({});
    setSaved(false);
  }, [isOpen, teacher]);

  // Self-feeding list: equipment already declared by any teacher
  const equipmentOptions = useMemo(() => Array.from(new Set(allTeachers.flatMap(t => t.privateEquipment))).sort(), [allTeachers]);
  const categories = teacherCategories({
    ...draft,
    id: '',
    hourlyRate: 0
  } as Teacher);
  const set = <K extends keyof Draft,>(key: K, value: Draft[K]) => {
    setDraft(d => ({
      ...d,
      [key]: value
    }));
    setErrors(e => ({
      ...e,
      [key]: ''
    }));
  };
  const toggleProduct = (code: string) => set('productCodes', draft.productCodes.includes(code) ? draft.productCodes.filter(c => c !== code) : [...draft.productCodes, code]);
  const addEquipment = () => {
    const value = equipmentInput.trim();
    if (!value) return;
    const existing = equipmentOptions.find(o => o.toLowerCase() === value.toLowerCase());
    const item = existing || value;
    if (!draft.privateEquipment.includes(item)) set('privateEquipment', [...draft.privateEquipment, item]);
    setEquipmentInput('');
  };
  const handleSave = () => {
    const next: Record<string, string> = {};
    if (!draft.firstName.trim()) next.firstName = 'Inserisci il nome';
    if (!draft.lastName.trim()) next.lastName = 'Inserisci il cognome';
    if (draft.productCodes.length === 0) next.productCodes = 'Seleziona almeno un corso erogabile';
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;
    teachersStore.save({
      ...draft,
      hourlyRate: draft.hourlyRate ?? 0
    });
    setSaved(true);
    setTimeout(onClose, 1300);
  };
  const sectionTitle = 'text-sm font-semibold text-seform-navy mb-4';
  return <Modal isOpen={isOpen} onClose={onClose} title={teacher ? 'Modifica docente' : 'Nuovo docente'} size="2xl" data-id="element-1581" data-mp="1isjepcghpnyn">
      <AnimatePresence mode="wait" data-id="element-1582" data-mp="19158ymxy3rx2">
        {saved ? <ModalSuccess title="Docente salvato" subtitle="Il docente è disponibile nell'assegnazione dei corsi abilitati" data-id="element-1583" data-mp="191oqw3nligb3" /> : <div className="space-y-6" data-id="element-1584" data-mp="100qn9mh2kat1">
            <section data-id="element-1585" data-mp="zznuf906ryah">
              <h4 className={sectionTitle} data-id="element-1586" data-mp="102a5vjq76xxz">Dati anagrafici</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1587" data-mp="rz9isy8lt7m6">
                <Input label="Nome" value={draft.firstName} onChange={e => set('firstName', e.target.value)} error={errors.firstName} required data-id="element-1588" data-mp="n0eqn5oqy6pe" />
                <Input label="Cognome" value={draft.lastName} onChange={e => set('lastName', e.target.value)} error={errors.lastName} required data-id="element-1589" data-mp="e6a1cqp9h3bz" />
                <Input label="CF" value={draft.fiscalCode} onChange={e => set('fiscalCode', e.target.value.toUpperCase())} maxLength={16} data-id="element-1590" data-mp="3vhja5i4p8lwi" />
                <Input label="Email" type="email" value={draft.email} onChange={e => set('email', e.target.value)} data-id="element-1591" data-mp="1tobuu8p364am" />
                <Input label="Telefono" value={draft.phone} onChange={e => set('phone', e.target.value)} data-id="element-1592" data-mp="37j25ontzey62" />
                <Input label="Zone coperte" placeholder="Es. Arezzo, Siena" value={draft.zones} onChange={e => set('zones', e.target.value)} data-id="element-1593" data-mp="3hbhnnl5a3mqy" />
                <div className="md:col-span-2" data-id="element-1594" data-mp="2oon5iuvxozgn">
                  <Input label="Disponibilità limitata" placeholder="Es. solo la mattina, tranne il mercoledì" value={draft.limitedAvailability} onChange={e => set('limitedAvailability', e.target.value)} data-id="element-1595" data-mp="2tmeuph5727h5" />
                </div>
              </div>
            </section>

            <section className="pt-6 border-t border-slate-100" data-id="element-1596" data-mp="hvkpyranofx1">
              <h4 className={sectionTitle} data-id="element-1597" data-mp="hxxghsuhbylp">Corsi erogabili</h4>
              {errors.productCodes && <p className="text-sm text-red-600 mb-3" role="alert" data-id="element-1598" data-mp="2e122uu2qfoov">
                  {errors.productCodes}
                </p>}
              <div className="space-y-4" data-id="element-1599" data-mp="2nxf021xhkkod">
                {trainingCategories.filter(c => trainingProducts.some(p => p.category === c)).map(category => <fieldset key={category} data-id="element-1600" data-mp="1v7pst6u6noeb">
                      <legend className="text-xs font-medium text-slate-500 mb-2" data-id="element-1601" data-mp="1v6eu3lflh6u2">{category}</legend>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2" data-id="element-1602" data-mp="1q7bp20zm70m3">
                        {trainingProducts.filter(p => p.category === category).map(p => <label key={p.code} className="flex items-start gap-2 cursor-pointer" data-id="element-1603" data-mp="1ch85xiw17n0v">
                              <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={draft.productCodes.includes(p.code)} onChange={() => toggleProduct(p.code)} data-id="element-1604" data-mp="17jot8nezfib1" />
                              <span className="text-sm text-slate-700" data-id="element-1605" data-mp="3e6w3lnrprzhh">
                                {p.name}
                                <span className="ml-1.5 font-mono text-xs text-slate-400" data-id="element-1606" data-mp="5r44vhx92e19">{p.code}</span>
                              </span>
                            </label>)}
                      </div>
                    </fieldset>)}
              </div>
              <div className="mt-4" data-id="element-1607" data-mp="1ozcemijfng7">
                <p className="text-xs font-medium text-slate-500 mb-2" data-id="element-1608" data-mp="1nxnpf8ln2g0">Macrocategorie (automatiche)</p>
                {categories.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-1609" data-mp="2mfpysu11tdlv">
                    {categories.map(c => <Badge key={c} variant="info" size="sm" data-id="element-1610" data-mp="3d6kxbdev283w">
                        {c}
                      </Badge>)}
                  </div> : <p className="text-sm text-slate-400" data-id="element-1611" data-mp="3454vhu243eba">Seleziona i corsi erogabili</p>}
              </div>
            </section>

            <section className="pt-6 border-t border-slate-100" data-id="element-1612" data-mp="2z7uz1ugb22dy">
              <h4 className={sectionTitle} data-id="element-1613" data-mp="p65virk2sfa">Condizioni</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1614" data-mp="qqzn661q7c3">
                <Input label="Tariffa oraria" type="number" min={0} step="0.5" value={draft.hourlyRate ?? ''} onChange={e => set('hourlyRate', parseAmount(e.target.value))} endContent={<span className="text-xs" data-id="element-1616" data-mp="jfxnf7lp5p4r">€/h</span>} data-id="element-1615" data-mp="3rwaf8kb6aqqv" />
                <Select label="Metodo di fatturazione" value={draft.billingMethod} onChange={e => set('billingMethod', e.target.value as TeacherBillingMethod)} options={billingMethods.map(m => ({
              value: m,
              label: m
            }))} data-id="element-1617" data-mp="enxl58i5mk50" />
                <div className="md:col-span-2" data-id="element-1618" data-mp="vwqb20qwi0lr">
                  <Textarea label="Accordi" value={draft.agreements} onChange={e => set('agreements', e.target.value)} data-id="element-1619" data-mp="w04fdej4kjvu" />
                </div>
                <div className="md:col-span-2" data-id="element-1620" data-mp="19x9hx4jmr0mc">
                  <Input label="Rimborso spese" value={draft.expenseReimbursement} onChange={e => set('expenseReimbursement', e.target.value)} data-id="element-1621" data-mp="19vyej5u3juvr" />
                </div>
                <div className="md:col-span-2" data-id="element-1622" data-mp="1omp4e8c4ojw8">
                  <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1623" data-mp="1oo08k99hjtnv">
                    Attrezzatura privata disponibile
                  </span>
                  <div className="flex gap-2" data-id="element-1624" data-mp="u54z0640o9l7">
                    <div className="flex-1" data-id="element-1625" data-mp="u6g3k85adldd">
                      <input list="teacher-equipment" aria-label="Aggiungi attrezzatura" className="block w-full rounded-xl border-2 border-slate-200 bg-slate-50/50 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-seform-orange focus:bg-white focus:ring-0 focus:outline-none" placeholder="Seleziona o scrivi nuova attrezzatura..." value={equipmentInput} onChange={e => setEquipmentInput(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addEquipment();
                    }
                  }} data-id="element-1626" data-mp="z36i2r1g08wo" />
                      <datalist id="teacher-equipment" data-id="element-1627" data-mp="3tktcen06t8tw">
                        {equipmentOptions.map(o => <option key={o} value={o} data-id="element-1628" data-mp="3m1sc0akonvar" />)}
                      </datalist>
                    </div>
                    <Button type="button" variant="flat" color="secondary" startContent={<Plus className="h-4 w-4" data-id="element-1630" data-mp="37cnxb3d3k97e" />} onClick={addEquipment} disabled={!equipmentInput.trim()} data-id="element-1629" data-mp="3cafm3oig3f7d">
                      Aggiungi
                    </Button>
                  </div>
                  {draft.privateEquipment.length > 0 && <div className="flex flex-wrap gap-2 mt-3" data-id="element-1631" data-mp="3uz2r670mg4ky">
                      {draft.privateEquipment.map(item => <span key={item} className="inline-flex items-center gap-1 rounded-full bg-slate-100 pl-3 pr-1 py-1 text-xs text-slate-700" data-id="element-1632" data-mp="2jlqtyezdak7r">
                          {item}
                          <button type="button" onClick={() => set('privateEquipment', draft.privateEquipment.filter(i => i !== item))} className="rounded-full p-0.5 hover:bg-slate-200" aria-label={`Rimuovi ${item}`} data-id="element-1633" data-mp="16nq9g6dq8obd">
                            <X className="h-3 w-3" data-id="element-1634" data-mp="mx4saz80d7pb" />
                          </button>
                        </span>)}
                    </div>}
                </div>
              </div>
            </section>

            <p className="text-xs text-slate-500" data-id="element-1635" data-mp="iwuo9s3s0rd3">
              La documentazione (CV, certificazioni, documenti identità) si carica dal dettaglio del docente.
            </p>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-1636" data-mp="8z6p8pz50xp4">
              <Button variant="flat" color="default" onClick={onClose} data-id="element-1637" data-mp="3lj2f83falkdn">
                Annulla
              </Button>
              <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-1639" data-mp="3hj1p3litzkqr" />} data-id="element-1638" data-mp="3hhyzri15pqcy">
                Salva docente
              </Button>
            </div>
          </div>}
      </AnimatePresence>
    </Modal>;
}